import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { Settings, Code2, Lightbulb, BarChart3, ArrowRight, Minimize2, X } from "lucide-react";

const CHIPS = [
  { icon: Lightbulb, a: "Ideas", b: "Project" },
  { icon: Code2, a: "Code", b: "Test" },
  { icon: BarChart3, a: "Explain", b: "Confidence" },
];

const Chip = ({ c, i }) => (
  <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.9 + i * 0.15, duration: 0.6 }}
    data-testid={`hero-chip-${i}`}
    className="flex items-center gap-3 rounded-xl border border-white/70 bg-white/90 px-3.5 py-2.5 text-[14px] font-medium text-[#0B1B33] shadow-[0_10px_30px_-14px_rgba(37,99,235,0.45)] backdrop-blur">
    <span className="grid h-7 w-7 place-items-center rounded-md bg-blue-100 text-blue-600"><c.icon className="h-4 w-4" /></span>
    {c.a} <ArrowRight className="h-3.5 w-3.5 text-slate-500" /> {c.b}
  </motion.div>
);

const Window = () => (
  <div className="relative rounded-2xl border border-white bg-gradient-to-br from-white to-blue-50/80 p-4 shadow-[0_40px_80px_-30px_rgba(37,99,235,0.5)]">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-1.5">
        <span className="grid h-4 w-4 place-items-center rounded-full bg-blue-600 text-[8px] font-bold text-white">a</span>
        <span className="h-2 w-2 rounded-full bg-rose-400" /><span className="h-2 w-2 rounded-full bg-amber-400" /><span className="h-2 w-2 rounded-full bg-emerald-400" />
      </div>
      <div className="flex gap-2 text-slate-500"><Minimize2 className="h-3.5 w-3.5" /><X className="h-3.5 w-3.5" /></div>
    </div>
    <div className="mt-5 flex items-center justify-around">
      <Settings className="spin-slow h-11 w-11 text-sky-500" strokeWidth={2.4} />
      <ArrowRight className="h-5 w-5 text-blue-500" />
      <div className="h-9 w-12 rounded-md bg-gradient-to-b from-sky-400 to-blue-600 shadow-lg" />
      <ArrowRight className="h-5 w-5 text-blue-500" />
      <div className="grid h-10 w-12 place-items-center rounded-md bg-blue-600 text-white shadow-lg"><Code2 className="h-5 w-5" /></div>
    </div>
    <div className="mt-5 grid grid-cols-[1fr_1.1fr] gap-4">
      <div className="space-y-2.5 rounded-lg bg-white p-3 shadow-sm">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="text-[11px] font-bold text-emerald-500">✓</span>
            <span className="h-1.5 rounded bg-slate-300" style={{ width: `${70 - i * 8}%` }} />
          </div>
        ))}
      </div>
      <div className="relative rounded-lg bg-white p-3 shadow-sm">
        <svg viewBox="0 0 100 50" className="h-full w-full">
          <polyline className="draw" points="5,42 25,30 40,36 60,18 78,24 95,6" fill="none" stroke="#2563EB" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </div>
  </div>
);

export const HeroMockup = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [9, -9]), { stiffness: 120, damping: 16 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 120, damping: 16 });
  const { scrollY } = useScroll();
  const chipsY = useTransform(scrollY, [0, 600], [0, -60]);
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  return (
    <div data-testid="hero-mockup-card" onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }}
      className="relative grid gap-5 [perspective:1200px] sm:grid-cols-[1fr_auto] sm:items-center">
      <motion.div initial={{ opacity: 0, scale: 0.92, rotateY: -18 }} animate={{ opacity: 1, scale: 1, rotateY: 0 }}
        transition={{ duration: 1.1, delay: 0.3, ease: [0.2, 0.75, 0.15, 1] }} style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        className="relative">
        <div className="absolute -inset-3 -z-10 rounded-3xl bg-gradient-to-br from-blue-400/40 via-sky-300/30 to-teal-300/30 blur-2xl" />
        <Window />
        <div className="pointer-events-none absolute -bottom-14 -left-2 hidden items-end gap-1 sm:flex lg:-left-24 lg:bottom-0">
          <p className="font-hand -rotate-12 text-[21px] leading-[1] text-[#0B1B33]">From your<br />experience<br />to a real AI project</p>
          <svg viewBox="0 0 80 50" className="h-10 w-16 text-[#0B1B33]"><path className="draw" d="M2 45 C 30 45, 55 35, 75 8" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" /><path d="M66 9 L76 6 L74 16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" /></svg>
        </div>
      </motion.div>
      <motion.div style={{ y: chipsY }} className="floaty flex flex-col gap-3 sm:mt-0 mt-2">
        {CHIPS.map((c, i) => <Chip key={c.a} c={c} i={i} />)}
      </motion.div>
    </div>
  );
};
